import { Handle, Position, useReactFlow } from "@xyflow/react";
import dynamicIconImports from "lucide-react/dynamicIconImports";
import React, { memo, useState, useRef, useEffect } from "react";

import { Link } from "react-router-dom";

import { cn } from "@/lib/utils";

import NodeIcon from "./node-icon";

interface CustomNodeProps {
  id: string;
  data: {
    name: string;
    icon: keyof typeof dynamicIconImports;
    formId?: number;
    type: number;
    originalType?: number; // Store original type before becoming start node
    isMultilingual?: boolean; // Track if node is multilingual (type 8)
    showSequence?: boolean; // Track if sequence should be shown
  };
}

const CustomNode: React.FC<CustomNodeProps> = ({ id, data }) => {
  const { name, icon, formId, type } = data;
  const { setNodes } = useReactFlow();
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  const isCondition = type === 3;
  const isStartNode = type === 4;
  const isFormNode = type === 1 || type === 8;
  const isMultilingualNode = type === 8;
  const showLines = isStartNode;

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setContextMenu({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleSetStartNode = () => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === id) {
          // Toggle only this node between start (4) and its original type
          if (n.data.type === 4) {
            // Currently a start node, revert to original type
            return {
              ...n,
              data: {
                ...n.data,
                type: n.data.originalType || 3
              }
            };
          } else {
            // Make this a start node, save original type
            return {
              ...n,
              data: {
                ...n.data,
                originalType: n.data.type,
                type: 4
              }
            };
          }
        }
        // Don't modify other nodes
        return n;
      })
    );
    setContextMenu(null);
  };

  const handleToggleShowSequence = () => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === id) {
          return {
            ...n,
            data: {
              ...n.data,
              showSequence: !n.data.showSequence
            }
          };
        }
        return n;
      })
    );
    setContextMenu(null);
  };

  const handleToggleMultilingual = () => {
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === id) {
          return {
            ...n,
            data: {
              ...n.data,
              isMultilingual: !n.data.isMultilingual
            }
          };
        }
        return n;
      })
    );
    setContextMenu(null);
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu(null);
    };

    if (contextMenu) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [contextMenu]);

  if (isCondition) {
    return (
      <div
        className="group relative"
        onContextMenu={handleContextMenu}
        style={{
          width: "120px",
          height: "120px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {/* Diamond shape */}
        <div
          className={cn(
            "absolute inset-0 border-2 border-primary bg-white shadow-md transition-all",
            isStartNode && "ring-2 ring-green-500"
          )}
          style={{
            clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)"
          }}
        />

        {showLines && (
          <div className="absolute -left-6 top-1/2 flex -translate-y-1/2 flex-col gap-1">
            <div className="h-0.5 w-3 bg-primary" />
            <div className="h-0.5 w-3 bg-primary" />
          </div>
        )}

        <div className="relative z-10 flex flex-col items-center justify-center gap-2">
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/90 [&_svg]:size-6 [&_svg]:text-slate-900",
              isStartNode && "ring-2 ring-green-500"
            )}
          >
            <NodeIcon name={icon} color="white" />
          </div>
          <p className="line-clamp-2 text-center text-sm font-bold">{name}</p>
        </div>

        <Handle
          id="a"
          type="target"
          position={Position.Top}
          isConnectable
          className="h-3 w-3 rounded-full !bg-slate-500 opacity-0 transition-opacity group-hover:opacity-100"
        />

        <Handle
          id="b"
          type="source"
          position={Position.Bottom}
          isConnectable
          className="h-3 w-3 rounded-full !bg-slate-500 opacity-0 transition-opacity group-hover:opacity-100"
        />

        {contextMenu && (
          <div
            ref={contextMenuRef}
            className="absolute right-0 top-0 z-50 rounded-md border border-gray-300 bg-white shadow-lg"
            style={{
              left: `${contextMenu.x}px`,
              top: `${contextMenu.y}px`
            }}
          >
            <button
              onClick={handleSetStartNode}
              className={cn(
                "block w-full px-4 py-2 text-left text-sm hover:bg-primary/10",
                isStartNode && "bg-green-100 font-semibold text-green-700"
              )}
            >
              {isStartNode ? "✓ Start Node" : "Set as Start Node"}
            </button>
            <button
              onClick={handleToggleShowSequence}
              className={cn(
                "block w-full border-t border-gray-300 px-4 py-2 text-left text-sm hover:bg-primary/10",
                data.showSequence && "bg-blue-100 font-semibold text-blue-700"
              )}
            >
              {data.showSequence ? "✓ نمایش در ترتیب" : "نمایش در ترتیب"}
            </button>
            {isFormNode && (
              <button
                onClick={handleToggleMultilingual}
                className={cn(
                  "block w-full border-t border-gray-300 px-4 py-2 text-left text-sm hover:bg-primary/10",
                  data.isMultilingual &&
                    "bg-amber-100 font-semibold text-amber-700"
                )}
              >
                {data.isMultilingual ? "✓ چند زبانه" : "چند زبانه"}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="group relative rounded-md border-2 border-primary bg-white px-4 py-2 shadow-md"
      onContextMenu={handleContextMenu}
    >
      {showLines && (
        <div className="absolute -left-4 top-1/2 flex -translate-y-1/2 flex-col gap-1">
          <div className="h-0.5 w-3 bg-primary" />
          <div className="h-0.5 w-3 bg-primary" />
        </div>
      )}

      <div className="flex items-center space-x-3">
        {isFormNode ? (
          <Link
            to={`/dashboard/form/editor/${formId}`}
            target="_blank"
            className={cn(
              "flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/90 [&_svg]:size-8 [&_svg]:text-slate-900",
              isStartNode && "ring-2 ring-green-500",
              isMultilingualNode && "ring-2 ring-amber-500"
            )}
          >
            <NodeIcon name={icon} color="white" />
          </Link>
        ) : (
          <div
            className={cn(
              "flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/90 [&_svg]:size-8 [&_svg]:text-slate-900",
              isStartNode && "ring-2 ring-green-500",
              isMultilingualNode && "ring-2 ring-amber-500"
            )}
          >
            <NodeIcon name={icon} color="white" />
          </div>
        )}
        <p className="text-lg font-bold">{name}</p>
      </div>

      <Handle
        id="a"
        type="target"
        position={Position.Left}
        isConnectable
        className="h-3 w-3 rounded-full !bg-slate-500 opacity-0 transition-opacity group-hover:opacity-100"
      />

      <Handle
        id="b"
        type="source"
        position={Position.Right}
        isConnectable
        className="h-3 w-3 rounded-full !bg-slate-500 opacity-0 transition-opacity group-hover:opacity-100"
      />

      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="absolute right-0 top-0 z-50 rounded-md border border-gray-300 bg-white shadow-lg"
          style={{
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`
          }}
        >
          <button
            onClick={handleSetStartNode}
            className={cn(
              "block w-full px-4 py-2 text-left text-sm hover:bg-primary/10",
              isStartNode && "bg-green-100 font-semibold text-green-700"
            )}
          >
            {isStartNode ? "✓ Start Node" : "Set as Start Node"}
          </button>
          <button
            onClick={handleToggleShowSequence}
            className={cn(
              "block w-full border-t border-gray-300 px-4 py-2 text-left text-sm hover:bg-primary/10",
              data.showSequence && "bg-blue-100 font-semibold text-blue-700"
            )}
          >
            {data.showSequence ? "✓ نمایش در ترتیب" : "نمایش در ترتیب"}
          </button>
          {isFormNode && (
            <button
              onClick={handleToggleMultilingual}
              className={cn(
                "block w-full border-t border-gray-300 px-4 py-2 text-left text-sm hover:bg-primary/10",
                data.isMultilingual &&
                  "bg-amber-100 font-semibold text-amber-700"
              )}
            >
              {data.isMultilingual ? "✓ چند زبانه" : "چند زبانه"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default memo(CustomNode);
